import type { App } from 'vue';
import { createI18n, type I18n } from 'vue-i18n';
// 移除DI依赖
import { getAreaConfig } from '../../utils/config';
import { BaseService } from '../base';

// i18n 服务配置
export interface I18nServiceConfig {
  defaultLocale?: string;
  fallbackLocale?: string;
  legacy?: boolean;
  globalInjection?: boolean;
  messages: Record<string, any>;
}

// i18n 服务实现
export class I18nService extends BaseService {
  private i18n: I18n | null = null;
  private config: I18nServiceConfig;

  constructor(config: I18nServiceConfig) {
    super('I18nService');
    this.config = {
      defaultLocale: 'zh-CN',
      fallbackLocale: 'zh-CN',
      legacy: false,
      globalInjection: true,
      ...config,
    };
  }

  // 初始化服务
  protected async onInitialize(app?: App): Promise<void> {
    if (typeof window !== 'undefined') {
      (window as any).I18nService = this;
    }

    this.i18n = this.createI18nInstance();

    // 如果提供了 Vue 应用实例，则安装 i18n
    if (app) {
      this.install(app);
    }
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    this.i18n = null;
  }

  // 创建 i18n 实例
  private createI18nInstance(): I18n {
    const { defaultLocale } = getAreaConfig();
    const locale = this.config.defaultLocale || defaultLocale;

    return createI18n({
      legacy: this.config.legacy,
      locale,
      fallbackLocale: this.config.fallbackLocale,
      messages: this.config.messages,
      globalInjection: this.config.globalInjection,
    });
  }

  // 安装到 Vue 应用
  install(app: App): void {
    if (!this.i18n) {
      throw new Error('I18nService is not initialized');
    }
    app.use(this.i18n);
  }

  // 获取 i18n 实例
  getI18n(): I18n {
    if (!this.i18n) {
      throw new Error('I18nService is not initialized');
    }
    return this.i18n;
  }

  // 获取当前语言
  getCurrentLocale(): string {
    const { defaultLocale } = getAreaConfig();
    return this.config.defaultLocale || defaultLocale;
  }

  // 切换语言
  async setLocale(locale: 'zh-CN' | 'en-US'): Promise<void> {
    const i18nInstance = this.getI18n();
    i18nInstance.global.locale = locale;
  }

  // 获取支持的语言列表
  getSupportedLocales(): Array<{ code: string; name: string }> {
    return [
      { code: 'zh-CN', name: '中文' }, // @xi18n-ignore
      { code: 'en-US', name: 'English' },
    ];
  }

  // 初始化语言包
  async initializeLocale(_locale: string): Promise<void> {
    const i18n = this.createI18nInstance();
    this.i18n = i18n;
  }

  // 翻译文本
  t = (key: string, params?: Record<string, any>): string => {
    const i18nInstance = this.getI18n();
    return i18nInstance.global.t.apply(i18nInstance.global, [key, params || {}, {}]);
  };
}

// 创建 i18n 服务实例
export const createI18nService = (config: I18nServiceConfig): I18nService => {
  return new I18nService(config);
};

// 全局 I18n 服务实例
let globalI18nService: I18nService | null = null;

// 初始化 I18n 服务
export const initI18nService = async (config: I18nServiceConfig): Promise<I18nService> => {
  if (!globalI18nService) {
    globalI18nService = createI18nService(config);
    await globalI18nService.initialize();
  }
  return globalI18nService;
};

// 获取 I18n 服务实例
export const getI18nService = (): I18nService => {
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    return (window as any).I18nService;
  }
  if (!globalI18nService) {
    throw new Error('I18nService not initialized. Call initI18nService first.');
  }
  return globalI18nService;
};
